import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { COMMA, ENTER, SEMICOLON } from '@angular/cdk/keycodes';
import { Store, Select } from '@ngxs/store';

import { EmailActions } from '../state/email/actions';
import { TokenService } from '../shared/services/token.service';
import { EmailService } from '../shared/services/email.service';
import { MatChipInputEvent } from '@angular/material/chips';
import { StakeholderEmail } from '../shared/model/stakeholder-email';
import * as EmailValidator from 'email-validator';
import { HistoryActions } from '../state/history/actions';
import { Signature } from '../shared/model/signature';
import { GetAttachments, DownloadFromS3 } from '../state/attachments/actions/attachments.actions';
import { combineLatest, Observable } from 'rxjs';
import { Attachment } from '../shared/model/attachment';
import { StakeholderAddress } from '../shared/model/stakeholder-address';
import { AddressType } from '../shared/types/address-type';
import { DialogService } from '../shared/services/dialog.service';
import { ProcessingActions } from '../state/processing/actions';

@Component({
  selector: 'app-email',
  templateUrl: './email.component.html',
  styleUrls: ['./email.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmailComponent implements OnInit {

  @Select(state => state.stakeholderEmail) stakeholderEmail$: Observable<StakeholderEmail>;
  @Select(state => state.attachments.isLoading) isLoading$: Observable<boolean>;
  @Select(state => state.processing.isEmailSending) isEmailSending$: Observable<boolean>;
  @Select(state => state.processing.isProcessing) isProcessing$: Observable<boolean>;
  @Select(state => state.processing.isEmailBatchSending) isEmailBatchSending$: Observable<boolean>;
  @Select(state => state.processing.error) processingError$: Observable<string>;
  @Select(state => state.processing.message) processingMessage$: Observable<string>;

  readonly separatorKeysCodes: number[] = [ENTER, COMMA, SEMICOLON];
  visible = true;
  selectable = true;
  removable = true;
  addOnBlur = true;
  signatures: Signature[];
  emailAttachments: Array<Attachment> = [];
  emailId: string = '';

  // number of bcc addresses used for sending batches of email requests
  readonly batchSize = 500;
  //readonly batchSize = 5; // for testing

  constructor(
    private route: ActivatedRoute,
    private store: Store,
    private tokenService: TokenService,
    private emailService: EmailService,
    private dialogService: DialogService
  ) { }

  ngOnInit() {
    this.stakeholderEmail$.subscribe(email => {
      console.log('🚀 ~ file: email.component.ts ~ line 54 ~ EmailComponent ~ ngOnInit ~ email', email);
    });
    combineLatest([
      this.route.params,
      this.route.queryParams
    ]).subscribe(([params, queryParams]) => {
      if(params.token) {
        this.tokenService.saveToken(params.token);
      }
      if (params.id) {
        localStorage.removeItem('projectId');
        this.tokenService.getTokenById(params.id).subscribe(() => {
          if (queryParams.messageId){
            this.store.dispatch(new EmailActions.GetEmailRecordMessageOnly(params.id, queryParams.messageId));
          } else {
            this.store.dispatch(new EmailActions.GetEmailRecord(params.id));
          }
          this.store.dispatch(new GetAttachments(params.id));
          this.store.dispatch(new HistoryActions.GetEmailList());
          this.store
          .select(state => state.attachments.attachments)
          .subscribe(attachments => {
            if (attachments[params.id]) {
              // need to spread the array on line 58 to create a local copy
              // store.select.subscribe gives a 'frozen' variable that doesn't work with the moveItemInArray() function
              this.emailAttachments = [...attachments[params.id].attachmentList];
              this.emailAttachments.sort((a, b) => a.Order - b.Order);
              this.emailId = params.id;
            }
          });
          this.emailService.getSignatures().subscribe((data)=>{
            this.signatures = data;
          })
        });
      }
    });
  }

  openAttachment(attachment) {
    this.store.dispatch(new DownloadFromS3(attachment));
  }

  validEmail(email) {
    return EmailValidator.validate(email);
  }

  onSubjectUpdate(Subject) {
    this.store.dispatch(new EmailActions.EditEmailProp({ Subject }));
  }

  onFromDisplayUpdate(FromDisplay) {
    this.store.dispatch(new EmailActions.EditEmailProp({ FromDisplay }));
  }

  onHeaderUpdate(Header) {
    this.store.dispatch(new EmailActions.EditEmailProp({ Header }));
  }

  onMessageUpdate(Message) {
    this.store.dispatch(new EmailActions.EditEmailProp({ Message }));
  }

  onFooterUpdate(Footer) {
    this.store.dispatch(new EmailActions.EditEmailProp({ Footer }));
  }

  saveEmail(email: StakeholderEmail): void {
    this.store.dispatch(new EmailActions.SaveEmailRecord());
  }

  sendEmail(email: StakeholderEmail): void {
    if (email.BccAddresses.length > this.batchSize){
      this.store.dispatch(new EmailActions.SendEmailBatches(this.batchSize));
    } else {
      this.store.dispatch(new EmailActions.SendEmail());
    }
  }


  openAttachments(email: StakeholderEmail): void {
    const dialogRef = this.dialogService.openAttachments(email);
    // check if orders have changed and popup confirmation
    // dialogRef.beforeClosed().subscribe();
  }

  clearTo(to: string[]){
    to ? to.forEach(address => this.store.dispatch(new EmailActions.RemoveTo(address))) : null;
    //to.forEach(address => this.store.dispatch(new EmailActions.RemoveTo(address)));
  }

  addTo(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;

    if ((value || '').trim()) {
      this.store.dispatch(new EmailActions.AddTo(value.trim()));
    }

    if (input) {
      input.value = '';
    }
  }

  removeTo(email: string): void {
    this.store.dispatch(new EmailActions.RemoveTo(email));
  }

  clearCc(cc: string[]){
    cc ? cc.forEach(address => this.store.dispatch(new EmailActions.RemoveCc(address))) : null;
    //cc.forEach(address => this.store.dispatch(new RemoveCc(address)));
  }

  addCc(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;

    if ((value || '').trim()) {
      this.store.dispatch(new EmailActions.AddCc(value.trim()));
    }

    if (input) {
      input.value = '';
    }
  }

  removeCc(email: string): void {
    this.store.dispatch(new EmailActions.RemoveCc(email));
  }

  clearBcc(bcc: StakeholderAddress[]){
    bcc ? bcc.forEach(address => this.store.dispatch(new EmailActions.RemoveBcc(address))) : null;
    //bcc.forEach(address => this.store.dispatch(new EmailActions.RemoveBcc(address)));
  }

  addBcc(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;

    if ((value || '').trim()) {
      var stakeaddress: StakeholderAddress = new StakeholderAddress;
      stakeaddress.StakeEmail = value.trim();
      this.store.dispatch(new EmailActions.AddBcc(stakeaddress));
    }

    if (input) {
      input.value = '';
    }
  }

  removeBcc(email: StakeholderAddress): void {
    this.store.dispatch(new EmailActions.RemoveBcc(email));
  }

  pasteAddresses(event: ClipboardEvent, type: AddressType){
    this.store.dispatch(new ProcessingActions.SetProcessing(true));
    event.preventDefault(); //Prevents the default action
    event.clipboardData
    .getData('Text')
    .split(/;|,|\n/)
    .forEach(value => {
      if(value.trim()){
        const trimmed = value.trim();
        switch(type.toLocaleLowerCase()){
          case "cc":
            this.store.dispatch(new EmailActions.AddCc(trimmed));
            break;
          case "bcc":
            this.store.dispatch(new EmailActions.AddBcc({
              StakeEmail: trimmed,
              StakeId: 0 // See *NOTE: StakeId:0 below
            }));
            break;
          case "to":
            this.store.dispatch(new EmailActions.AddTo(trimmed));
            break;
          default:
            console.warn('unhandled type ->', type);
        }
      }
    })
    setTimeout(() => {
      this.store.dispatch(new ProcessingActions.SetProcessing(false));
    }, 2000);
  }

  // *NOTE: StakeId:0
  /*
  stakeid is how we send information back to the main PIMA api that a stakeholder has been invited.
  It's used for event tracking.  MassDOT uses this.  Not sure who else uses it.
  It sends a message to SNS, which posts the stake id(s) to the PIMA api and records the event invite in the database.
  So setting it to 0 would have no affect other than to not track the invites.
  */

}
